/**
 * PullToRefresh — 자식 컨텐츠 위에 당겨서 새로고침 인터랙션을 얹는 wrapper.
 *
 * 사용:
 *   <PullToRefresh onRefresh={() => refetch()}>
 *     <YourPageBody />
 *   </PullToRefresh>
 *
 * 동작:
 *   - 자식 영역에서 시작된 터치만 추적 — 다른 페이지 영역(예: 상단 고정 헤더)에서 시작된 터치는 무시.
 *   - 가장 가까운 scrollable 조상이 scrollTop===0 인 상태에서 아래로 끌 때만 발동.
 *   - threshold 도달 후 손가락을 떼면 onRefresh 호출, Promise 종료까지 인디케이터 잠금.
 *
 * 모듈성:
 *   - 어떤 page 컴포넌트 안에서도 wrap 만 하면 동작. layout 변경 불필요.
 *   - 페이지가 자체 css.page 같은 컨테이너를 갖고 있으면 그 안쪽을 감싸는 게 일반적.
 */
import { useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { usePullToRefresh } from "../../hooks/usePullToRefresh";
import styles from "./PullToRefresh.module.css";

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => void | Promise<unknown>;
  /** default 70 */
  threshold?: number;
  /** default 120 */
  maxDistance?: number;
  /** default 2.5 */
  resistance?: number;
  /** false 면 인터랙션 비활성. default true */
  enabled?: boolean;
  className?: string;
}

export default function PullToRefresh({
  children,
  onRefresh,
  threshold,
  maxDistance,
  resistance,
  enabled,
  className,
}: PullToRefreshProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const { pullDistance, isRefreshing, isPulling, progress } = usePullToRefresh(ref, {
    onRefresh,
    threshold,
    maxDistance,
    resistance,
    enabled,
  });

  const showIndicator = pullDistance > 0 || isRefreshing;
  // 인디케이터(리로드 아이콘)만 위에서 내려온다 — 콘텐츠는 고정.
  const indicatorTransform = `translateY(${pullDistance}px)`;
  // 비-진행 상태에서는 progress(0..1) 에 맞춰 화살표처럼 0~180° 회전.
  const idleSpinnerRotation = `rotate(${Math.min(180, progress * 180)}deg)`;

  return (
    <div ref={ref} className={className ? `${styles.container} ${className}` : styles.container}>
      <div
        className={styles.indicator}
        style={{
          opacity: showIndicator ? Math.min(1, progress + 0.2) : 0,
          transform: indicatorTransform,
          transition: isPulling ? "none" : "transform 0.25s ease-out, opacity 0.2s ease-out",
        }}
        aria-hidden={!showIndicator}
      >
        {/* Chrome 식 — 흰 원형 디스크(그림자) 위에 스피너가 떠 있다. */}
        <div className={styles.disc}>
          <div
            className={`${styles.spinner} ${isRefreshing ? styles.spinnerSpinning : styles.spinnerIdle}`}
            style={isRefreshing ? undefined : { transform: idleSpinnerRotation }}
            role={isRefreshing ? "status" : undefined}
            aria-label={isRefreshing ? t("state.refreshing") : undefined}
          />
        </div>
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  );
}
